import _extends from '@babel/runtime/helpers/extends';
import React__default, { useState, useRef, useMemo, useCallback, useEffect, createElement } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { usePopper } from 'react-popper';
import mergeRefs from 'react-merge-refs';
import Overlay from '../Overlay/Overlay.js';
import { getTransformOrigin, getPrimaryPlacement } from '../../utils/popover.js';
import { isKeyboardClick, ENTER, SPACE, ARROW_DOWN, ARROW_UP, ESCAPE, TAB } from '../../utils/keyboard.js';
import useResizeObserver from '../../hooks/useResizeObserver.js';
import Spinner from '../Spinner/Spinner.js';
import Arrow, { POPOVER_ARROW_SVG_SIZE } from './Arrow.js';

const POPOVER_PROPS = {
  placement: ['top', 'top-start', 'top-end', 'right', 'right-start', 'right-end', 'bottom', 'bottom-start', 'bottom-end', 'left', 'left-start', 'left-end', 'auto', 'auto-start', 'auto-end'],
  interactionKind: ['click', 'click-target', 'hover', 'hover-target', 'menu'],
  positioningStrategy: ['absolute', 'fixed']
};
const BASE_CLASS = 'nui-popover';
const Classes = {
  BASE: BASE_CLASS,
  TARGET: `${BASE_CLASS}-target`,
  CONTENT: `${BASE_CLASS}-content`,
  TRANSITION_CONTAINER: `${BASE_CLASS}-transition-container`,
  DISMISS: `${BASE_CLASS}-dismiss`,
  OPEN: `${BASE_CLASS}--open`,
  DISABLED: `${BASE_CLASS}--disabled`,
  MINIMAL: `${BASE_CLASS}--minimal`,
  LOADING_CONTAINER: `${BASE_CLASS}-loading-container`
};
const popoverPropTypes = {
  /** should the overlay acquire focus on open */
  autoFocus: PropTypes.node,
  /** element which will trigger the popover and be it's target */
  children: PropTypes.node,
  /** optional classes for the target wrapper */
  className: PropTypes.string,
  /** the content displayed inside the popover */
  content: PropTypes.node,
  /** whether the content is still being loaded */
  contentLoading: PropTypes.bool,
  /** initial opened state when uncontrolled */
  defaultIsOpen: PropTypes.bool,
  /** prevents from showing */
  disabled: PropTypes.bool,
  /** whether there is no content to render */
  emptyContent: PropTypes.bool,
  /** keep focus inside overlay when opened */
  enforceFocus: PropTypes.bool,
  /** toggles the visibility of the backdrop behind the contents */
  hasBackdrop: PropTypes.bool,
  /** time in milliseconds the popover should remain open after the user hovers off the trigger */
  hoverCloseDelay: PropTypes.number,
  /** time in milliseconds the popover should wait before opening after the user hovers over the trigger */
  hoverOpenDelay: PropTypes.number,
  /** the kind of interaction that triggers the display of the popover */
  interactionKind: PropTypes.oneOf(POPOVER_PROPS.interactionKind),
  /** puts popover in controlled mode when passed */
  isOpen: PropTypes.bool,
  /** will not show arrow and uses subtler animation */
  minimal: PropTypes.bool,
  /** overrides for Popper.js modifiers */
  modifiers: PropTypes.shape({
    arrow: PropTypes.shape({
      enabled: PropTypes.bool,
      options: PropTypes.shape({})
    }),
    offset: PropTypes.shape({
      enabled: PropTypes.bool,
      options: PropTypes.shape({})
    }),
    computeStyles: PropTypes.shape({
      options: PropTypes.shape({})
    }),
    flip: PropTypes.shape({
      options: PropTypes.shape({})
    }),
    preventOverflow: PropTypes.shape({
      options: PropTypes.shape({})
    })
  }),
  /** invoked when user interaction causes the popover to close */
  onClose: PropTypes.func,
  /** user interaction callback when in controlled mode */
  onInteraction: PropTypes.func,
  /** allow to open on Enter key (used with interactionKind="menu")  */
  openOnEnterKey: PropTypes.bool,
  /** placement relative to the target at which the popover should appear */
  placement: PropTypes.oneOf(POPOVER_PROPS.placement),
  /** optional classes for the popover element */
  popoverClassName: PropTypes.string,
  /** Ref to the popover element */
  popoverRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({})]),
  /** Can be used to manually reposition the popover  */
  popperUpdateRef: PropTypes.func,
  /** Controls the aria-haspopup attribute of the target element. */
  popupKind: PropTypes.string,
  /** Popper [positioning strategy](https://popper.js.org/docs/v2/constructors/#strategy) */
  positioningStrategy: PropTypes.oneOf(POPOVER_PROPS.positioningStrategy),
  /** custom function to render target element */
  renderTarget: PropTypes.func,
  /** Whether popover should be closed when reference becomes out of view */
  shouldCloseWhenReferenceIsHidden: PropTypes.bool,
  /** Whether popover elemet should be hidden when reference becomes out of view
   * (ignored if shouldCloseWhenReferenceIsHidden is true)
   */
  shouldHideWhenReferenceIsHidden: PropTypes.bool,
  /**
   * should the application return focus to the last active element in the document after popover closes,
   * always set to false for hover interactionKind
   * */
  shouldRepositionOnPopperElementResize: PropTypes.bool,
  shouldReturnFocusOnClose: PropTypes.bool,
  /** HTML tag name for the target element */
  targetTagName: PropTypes.oneOf(['span', 'div']),
  /** how long (in milliseconds) the overlay's enter/leave transition takes */
  transitionDuration: PropTypes.number,
  /** whether the popover should be wrapped in a Portal */
  usePortal: PropTypes.bool
};
/**
 * Displays floating content next to a target element.
 * Built on top of [Popper.js](https://popper.js.org/) library.
 */
function Popover({
  autoFocus,
  children,
  className,
  content,
  contentLoading = false,
  defaultIsOpen = false,
  disabled = false,
  emptyContent = false,
  enforceFocus,
  hasBackdrop = false,
  hoverCloseDelay = 300,
  hoverOpenDelay = 150,
  interactionKind = 'click',
  isOpen: isOpenProp,
  minimal = false,
  modifiers,
  onClose,
  onInteraction,
  openOnEnterKey,
  placement = 'auto',
  popoverClassName,
  popoverRef: popoverRefProp,
  popperUpdateRef,
  popupKind,
  positioningStrategy = 'absolute',
  renderTarget: renderTargetProp,
  shouldReturnFocusOnClose = false,
  targetTagName = 'span',
  usePortal,
  shouldHideWhenReferenceIsHidden = false,
  shouldCloseWhenReferenceIsHidden = true,
  shouldRepositionOnPopperElementResize = true,
  ...rest
}) {
  const [isOpen, setIsOpen] = useState(isOpenProp ?? defaultIsOpen);
  const [targetElement, setTargetElement] = useState(null);
  const [popperElement, setPopperElement] = useState(null);
  const [popperArrow, setPopperArrow] = useState(null);
  const isMouseInTargetOrPopover = useRef();
  const openTimeout = useRef();
  const popoverRef = useRef(null);
  const interactionHandlerRef = useRef(onInteraction);
  const isArrowEnabled = useMemo(() => !minimal && modifiers?.arrow?.enabled !== false, [minimal, modifiers]);
  const getPopperModifiers = () => [{
    name: 'arrow',
    enabled: isArrowEnabled,
    ...modifiers?.arrow,
    options: {
      element: popperArrow,
      ...modifiers?.arrow?.options
    }
  }, {
    name: 'offset',
    enabled: isArrowEnabled,
    ...modifiers?.offset,
    options: {
      offset: [0, POPOVER_ARROW_SVG_SIZE / 2],
      ...modifiers?.offset?.options
    }
  }, {
    name: 'computeStyles',
    ...modifiers?.computeStyles,
    options: {
      gpuAcceleration: false,
      ...modifiers?.computeStyles?.options
    }
  }, {
    name: 'flip',
    ...modifiers?.flip
  }, {
    name: 'preventOverflow',
    ...modifiers?.preventOverflow,
    options: {
      altAxis: true,
      ...modifiers?.preventOverflow?.options
    }
  }, ...Object.entries(modifiers || {}).filter(([name]) => !['arrow', 'offset', 'computeStyles', 'flip', 'preventOverflow'].includes(name)).map(([name, value]) => ({
    name,
    ...value
  }))];
  const {
    styles: {
      popper: popperStyles,
      arrow: arrowStyles
    },
    attributes: {
      popper: popperProps
    },
    state: popperState,
    update
  } = usePopper(targetElement, popperElement, {
    placement,
    strategy: positioningStrategy,
    modifiers: getPopperModifiers()
  });
  const isHoverInteractionKind = useMemo(() => interactionKind.startsWith('hover'), [interactionKind]);
  const realPlacement = popperState?.placement ?? placement;
  const defaultAutoFocus = isHoverInteractionKind || interactionKind === 'menu' ? false : undefined;
  const defaultEnforceFocus = interactionKind === 'menu' ? false : undefined;
  const isReferenceHidden = !!popperProps?.['data-popper-reference-hidden'];
  const updateRef = useRef(update);
  const handlePopperElementResize = useCallback(() => {
    updateRef.current?.();
  }, []);
  useResizeObserver(popperElement, handlePopperElementResize, {
    enabled: !!popperElement && shouldRepositionOnPopperElementResize
  });
  useEffect(() => {
    if (typeof isOpenProp === 'boolean') setIsOpen(isOpenProp);
  }, [isOpenProp]);
  useEffect(() => {
    if (disabled && typeof isOpenProp !== 'boolean') setIsOpen(false);
  }, [disabled]);

  // Store onInteraction callback in ref to avoid situations when the callback is outdated after timeout
  useEffect(() => {
    interactionHandlerRef.current = onInteraction;
  }, [onInteraction]);
  useEffect(() => {
    popperUpdateRef?.(update);
  }, [update, popperUpdateRef]);
  useEffect(() => {
    updateRef.current = update;
  });
  const getPopoverElement = () => popperElement?.querySelector(`.${BASE_CLASS}`);
  const isElementInPopover = element => getPopoverElement()?.contains(element) ?? false;
  const setOpenState = (value, e, timeout = 0) => {
    if (openTimeout.current) clearTimeout(openTimeout.current);
    if (timeout) {
      openTimeout.current = setTimeout(() => setOpenState(value, e), timeout);
      return;
    }
    if (typeof isOpenProp === 'boolean') {
      interactionHandlerRef.current?.(value, e);
    } else {
      setIsOpen(value);
    }
    if (!value) {
      onClose?.(e);
    }
  };
  useEffect(() => {
    if (shouldCloseWhenReferenceIsHidden && isReferenceHidden) {
      setOpenState(false);
    }
  }, [shouldCloseWhenReferenceIsHidden, isReferenceHidden]);
  const handleTargetClick = e => {
    if (disabled || isElementInPopover(e.target)) return;
    setOpenState(!isOpen, e);
  };
  const handleTargetKeyDown = e => {
    if (interactionKind !== 'menu') return;
    if ([ENTER, SPACE].includes(e.key)) e.preventDefault();
    if ([ARROW_DOWN, ARROW_UP, SPACE, ENTER].includes(e.key) && !isOpen) {
      e.preventDefault();
      if (e.key !== ENTER || openOnEnterKey) handleTargetClick(e);
    }
    if (e.key === ESCAPE && isOpen) {
      handleTargetClick(e);
      e.stopPropagation();
      e.preventDefault();
    }
    if (e.key === TAB && isOpen && !(enforceFocus ?? defaultEnforceFocus ?? true)) {
      handleTargetClick(e);
    }
  };
  const handlePopoverClick = e => {
    const target = e.target;
    const eventPopover = target.closest(`.${Classes.BASE}`);
    const isEventFromSelf = eventPopover === getPopoverElement();
    const dismissElement = target.closest(`.${Classes.DISMISS}`);
    if (isEventFromSelf && dismissElement) setOpenState(false, e);
  };
  const handleMouseLeave = e => {
    isMouseInTargetOrPopover.current = false;
    setTimeout(() => {
      if (isMouseInTargetOrPopover.current) return;
      setOpenState(false, e, hoverCloseDelay);
    });
  };
  const handleMouseEnter = e => {
    isMouseInTargetOrPopover.current = true;
    if (!usePortal && isElementInPopover(e.target) && interactionKind === 'hover-target') {
      handleMouseLeave(e);
    } else if (!disabled) {
      setOpenState(true, e, hoverOpenDelay);
    }
  };
  const handleTargetContextMenu = e => {
    setOpenState(false, e);
  };
  const handleOverlayClose = e => {
    const nativeEvent = e instanceof Event ? e : e.nativeEvent;
    const eventTarget = e.target;
    // if click was in target, target event listener will handle things, so don't close
    if (!(targetElement === eventTarget || targetElement?.contains(eventTarget)) || nativeEvent instanceof KeyboardEvent) {
      setOpenState(false, e);
    }
  };
  const renderTarget = () => {
    const targetEvents = isHoverInteractionKind ? {
      // onBlur: handleTargetBlur,
      onContextMenu: handleTargetContextMenu,
      // onFocus: handleTargetFocus,
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave
    } : {
      onClick: handleTargetClick,
      onKeyDown: handleTargetKeyDown
    };
    const targetProps = {
      'aria-haspopup': popupKind ?? interactionKind === 'hover-target' ? undefined : 'true',
      className: cx(className, Classes.TARGET, {
        [Classes.OPEN]: isOpen,
        [Classes.DISABLED]: disabled
      }),
      ref: setTargetElement,
      ...targetEvents
    };
    if (renderTargetProp) {
      return renderTargetProp(targetProps, isOpen);
    }
    return /*#__PURE__*/createElement(targetTagName, targetProps, React__default.Children.toArray(children)[0]);
  };
  const renderPopover = () => {
    const transformOrigin = getTransformOrigin(realPlacement, isArrowEnabled ? arrowStyles : undefined);
    const popoverClasses = cx(Classes.BASE, {
      [Classes.MINIMAL]: minimal
    }, `${BASE_CLASS}--placement-${getPrimaryPlacement(realPlacement)}`, popoverClassName);
    const popoverHandlers = {
      onClick: handlePopoverClick,
      onKeyDown: e => {
        if (isKeyboardClick(e)) handlePopoverClick(e);
      }
    };
    if (interactionKind === 'hover' || !usePortal && interactionKind === 'hover-target') {
      popoverHandlers.onMouseEnter = handleMouseEnter;
      popoverHandlers.onMouseLeave = handleMouseLeave;
    }
    const hiddenClass = isReferenceHidden && shouldHideWhenReferenceIsHidden && !shouldCloseWhenReferenceIsHidden && 'nui-hidden';
    return /*#__PURE__*/React__default.createElement(Overlay, _extends({}, rest, {
      isOpen: isOpen,
      hasBackdrop: hasBackdrop,
      autoFocus: autoFocus ?? defaultAutoFocus,
      enforceFocus: enforceFocus ?? defaultEnforceFocus,
      canOutsideClickClose: interactionKind === 'click' || interactionKind === 'menu',
      shouldReturnFocusOnClose: isHoverInteractionKind ? false : shouldReturnFocusOnClose,
      transitionName: BASE_CLASS,
      onClose: handleOverlayClose,
      usePortal: usePortal
    }), /*#__PURE__*/React__default.createElement("div", _extends({
      className: cx(Classes.TRANSITION_CONTAINER, hiddenClass),
      ref: setPopperElement,
      style: popperStyles
    }, popperProps), /*#__PURE__*/React__default.createElement("div", _extends({
      className: popoverClasses,
      style: {
        transformOrigin
      },
      ref: mergeRefs([popoverRefProp, popoverRef])
    }, popoverHandlers), isArrowEnabled && /*#__PURE__*/React__default.createElement(Arrow, {
      arrowProps: {
        ref: setPopperArrow,
        style: arrowStyles
      },
      placement: realPlacement
    }), /*#__PURE__*/React__default.createElement("div", {
      className: Classes.CONTENT
    }, contentLoading && /*#__PURE__*/React__default.createElement("div", {
      className: Classes.LOADING_CONTAINER
    }, /*#__PURE__*/React__default.createElement(Spinner, {
      small: true
    })), !contentLoading && !emptyContent && content))));
  };
  return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, renderTarget(), renderPopover());
}
Popover.propTypes = popoverPropTypes;
Popover.displayName = 'NuiPopover';

export { Classes, POPOVER_PROPS, Popover as default };
